import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import { toast } from 'react-toastify';

import QuantityPicker from '../../../common/QuantityPicker';

import { fetchUpdatePhysicalSetOrder, fetchGetSKUSets } from '../../../../actions/items';
import { showModal } from '../../../../actions/modal';

import { getYandexStoreImageMin, parseError } from '../../../../helpers';

import { PHYSICAL_SET_ORDER_ACTION_TYPES } from '../../../../config';
import Adaptive from '../../../common/Adaptive';
import DesktopComponent from './DesktopComponent';
import MobileComponent from './MobileComponent';
import isMobile from 'ismobilejs';
import RenderPmIcon from './component/RenderPmIcon';

const SinglePhysicalSetOrder = (props) => {
  const [action, setAction] = useState(PHYSICAL_SET_ORDER_ACTION_TYPES[0].value);
  const [currentPackingMaterials, setCurrentPackingMaterials] = useState([]);
  const warehouses = useSelector((state) => state.orders.warehouses);

  const warehouseOptions = useMemo(
    () =>
      warehouses?.map((warehouse) => ({
        value: warehouse.code,
        label: warehouse.title,
        isDefault: warehouse.isDefault,
        isOptionDisabled:
          action === 'ASSEMBLY' && warehouse.goodsCondition === 'DEFECTIVE' ? true : undefined,
      })),
    [warehouses, action],
  );

  const defaultWarehouse = warehouseOptions?.find((w) => w.isDefault) ?? null;

  const [currentSet, setCurrentSet] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const [warehouseCode, setWarehouseCode] = useState(defaultWarehouse);

  useEffect(() => {
    const isDefectiveWarehouse =
      warehouses?.find((w) => w.code === warehouseCode.value)?.goodsCondition === 'DEFECTIVE';
    if (isDefectiveWarehouse && action === 'ASSEMBLY') {
      setWarehouseCode(defaultWarehouse);
    }
  }, [action]);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    (async function () {
      const parsedQuery = qs.parse(props.location?.search) || {};

      if (parsedQuery['?addSet']) {
        await dispatch(fetchGetSKUSets({ id: parsedQuery['?addSet'] })).then(({ skuSets }) => {
          setCurrentSet(skuSets[0]);
          setQuantity(1);
        });
      }

      if (parsedQuery['currentActionType']) {
        setAction(parsedQuery['currentActionType']);
      }
    })();
  }, []);

  const handleChooseSets = () => {
    dispatch(
      showModal('PhysicalSetsToTask', {
        currentActionType: action,
        warehouse: warehouseCode.value,
        submitCallback: (data) => {
          setCurrentSet(data);
          setQuantity(1);
        },
      }),
    );
  };

  const { usePackingMaterials } = useSelector((state) => state.auth.account);

  const handlePackingModal = (item = []) => {
    dispatch(
      showModal('ManagePackingMaterials', {
        item,
        id: currentSet.productId,
        items: currentSet.items,
        type: 'physicalOrder',
        currentPackingMaterials,
        setCurrentPackingMaterials,
      }),
    );
  };

  const handleSaveTask = async () => {
    setLoading(true);

    const finalPackingCommonMaterials =
      currentPackingMaterials.length !== 0
        ? currentPackingMaterials.commonMaterials.filter((item) => item.quantity !== 0)
        : null;
    const finalPackingCustomMaterials =
      currentPackingMaterials.length !== 0
        ? currentPackingMaterials.customMaterials.filter((item) => item.quantity !== 0)
        : null;

    const finalPackingMaterials = {
      commonMaterials: finalPackingCommonMaterials,
      customMaterials: finalPackingCustomMaterials,
    };

    const reqData = {
      quantity,
      action,
      warehouseCode: warehouseCode.value,
      variantId: currentSet.variantId,
      packingMaterials: finalPackingMaterials,
    };

    await dispatch(fetchUpdatePhysicalSetOrder(reqData))
      .then(() => {
        toast.info('Задание успешно создано');
        history.push('/physical-sets-journal');
      })
      .catch((err) => {
        toast.error(parseError(err));
        setLoading(false);
      });
  };

  const updatedCols =
    usePackingMaterials === true
      ? [null, 'Комплект', 'Состав', 'Кол-во', 'Упаковочные материалы']
      : [null, 'Комплект', 'Состав', 'Кол-во'];

  const tableData = {
    cols: updatedCols,
    rows: currentSet
      ? usePackingMaterials === true
        ? [
            {
              key: currentSet.id,
              data: [
                {
                  value: currentSet.images ? (
                    currentSet.images[0].url ? (
                      <div
                        className="image-block"
                        style={{
                          backgroundImage: `url(${getYandexStoreImageMin(
                            currentSet.images[0].url,
                            100,
                          )})`,
                        }}
                      />
                    ) : null
                  ) : null,
                  className: 'image-td',
                },
                {
                  value: (
                    <>
                      <div>{currentSet.shortName}</div>
                      <span className="ids-span">
                        {currentSet.id}
                        {currentSet?.extId ?? null}
                      </span>
                    </>
                  ),
                  className: 'shortname-td',
                },
                {
                  value: (
                    <table className="set-items-wrapper">
                      {currentSet.items.map((i, index) => (
                        <tr key={i.id} className="item-inner-table">
                          <td className="shortname-td">
                            <div>{i.shortName}</div>
                            <span className="ids-span">
                              {i.id}
                              {i?.extId ?? null}
                            </span>
                          </td>
                          <td className="quantity-td" align="right">{`${i.quantity} шт`}</td>
                        </tr>
                      ))}
                    </table>
                  ),
                  className: 'set-td',
                },
                {
                  value: (
                    <QuantityPicker
                      className="quantity-picker"
                      reverse
                      value={quantity}
                      onChange={setQuantity}
                      prefix=" шт."
                    />
                  ),
                  className: 'quantity-td',
                },
                {
                  value: (
                    <RenderPmIcon
                      usePackingMaterials={usePackingMaterials}
                      currentPackingMaterials={currentPackingMaterials}
                      handlePackingModal={handlePackingModal}
                      currentSet={currentSet}
                    />
                  ),
                  className: 'quantity-td',
                },
              ],
            },
          ]
        : [
            {
              key: currentSet.id,
              data: [
                {
                  value: currentSet.images ? (
                    currentSet.images[0].url ? (
                      <div
                        className="image-block"
                        style={{
                          backgroundImage: `url(${getYandexStoreImageMin(
                            currentSet.images[0].url,
                            100,
                          )})`,
                        }}
                      />
                    ) : null
                  ) : null,
                  className: 'image-td',
                },
                {
                  value: (
                    <>
                      <div>{currentSet.shortName}</div>
                      <span className="ids-span">
                        {currentSet.id}
                        {currentSet?.extId ?? null}
                      </span>
                    </>
                  ),
                  className: 'shortname-td',
                },
                {
                  value: (
                    <table className="set-items-wrapper">
                      {currentSet.items.map((i, index) => (
                        <tr key={i.id} className="item-inner-table">
                          <td className="shortname-td">
                            <div>{i.shortName}</div>
                            <span className="ids-span">
                              {i.id}
                              {i?.extId ?? null}
                            </span>
                          </td>
                          <td className="quantity-td" align="right">{`${i.quantity} шт`}</td>
                        </tr>
                      ))}
                    </table>
                  ),
                  className: 'set-td',
                },
                {
                  value: (
                    <QuantityPicker
                      className="quantity-picker"
                      reverse
                      value={quantity}
                      onChange={setQuantity}
                      prefix=" шт."
                    />
                  ),
                  className: 'quantity-td',
                },
              ],
            },
          ]
      : [],
  };

  const adaptiveProps = {
    action,
    setAction,
    currentSet,
    data: isMobile().any ? currentSet : tableData,
    loading,
    handleSaveTask,
    handleChooseSets,
    quantity,
    setQuantity,
    warehouseOptions,
    warehouseCode,
    handleChangeWarehouseCode: (value) => setWarehouseCode(value),
  };

  return (
    <Adaptive
      DekstopComponent={DesktopComponent}
      MobileComponent={MobileComponent}
      adaptiveProps={adaptiveProps}
    />
  );
};

export default SinglePhysicalSetOrder;
